import { msg } from "@lingui/core/macro";
import { ChevronLeft, ChevronRight } from "lucide-solid";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { cn, getMonthMatrix, isSameLocalDay, toLocalDateKey } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";

const DAYS_IN_WEEK = 7;
const REFERENCE_YEAR = 2024;
const WEEKDAY_REFERENCE_MONDAY = new Date(REFERENCE_YEAR, 0, 1); // a known Monday

interface CalendarProps {
  /** Currently selected day (local), or null. */
  selected: () => Date | null;
  onSelect: (date: Date) => void;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

/** Accessible month calendar with full keyboard navigation (ARIA grid). */
export function Calendar(props: CalendarProps) {
  const { t, locale } = useI18n();
  const today = startOfDay(new Date());
  // One-time snapshot to seed the visible month / focus; the panel remounts when
  // reopened, so it never needs to react to later `selected` changes here.
  const initial = untrack(() => props.selected()) ?? today;
  const [view, setView] = createSignal({ year: initial.getFullYear(), month: initial.getMonth() });
  const [focused, setFocused] = createSignal(startOfDay(initial));

  createEffect(() => {
    const sel = props.selected();
    if (sel) {
      const normalized = startOfDay(sel);
      setView({ year: normalized.getFullYear(), month: normalized.getMonth() });
      setFocused(normalized);
    }
  });
  const weeks = createMemo(() => getMonthMatrix(view().year, view().month));

  const weekdayLabels = createMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale(), { weekday: "short" });
    return Array.from({ length: DAYS_IN_WEEK }, (_, index) =>
      fmt.format(addDays(WEEKDAY_REFERENCE_MONDAY, index)),
    );
  });

  const monthLabel = createMemo(() =>
    new Intl.DateTimeFormat(locale(), { month: "long", year: "numeric" }).format(
      new Date(view().year, view().month, 1),
    ),
  );

  const fullDateLabel = (date: Date) =>
    new Intl.DateTimeFormat(locale(), { dateStyle: "full" }).format(date);

  const shiftMonth = (delta: number) => {
    setView((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const [gridRef, setGridRef] = createSignal<HTMLDivElement>();

  const moveFocus = (date: Date) => {
    const normalized = startOfDay(date);
    setFocused(normalized);
    if (normalized.getMonth() !== view().month || normalized.getFullYear() !== view().year) {
      setView({ year: normalized.getFullYear(), month: normalized.getMonth() });
    }
    // Focus the matching cell after the grid re-renders. The grid element is
    // stable, so capture it now and query it once the microtask runs.
    const grid = gridRef();
    queueMicrotask(() => {
      const key = toLocalDateKey(normalized);
      grid?.querySelector<HTMLButtonElement>(`[data-day="${key}"]`)?.focus();
    });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const handlers: Record<string, () => void> = {
      ArrowLeft: () => {
        moveFocus(addDays(focused(), -1));
      },
      ArrowRight: () => {
        moveFocus(addDays(focused(), 1));
      },
      ArrowUp: () => {
        moveFocus(addDays(focused(), -DAYS_IN_WEEK));
      },
      ArrowDown: () => {
        moveFocus(addDays(focused(), DAYS_IN_WEEK));
      },
      PageUp: () => {
        moveFocus(new Date(focused().getFullYear(), focused().getMonth() - 1, focused().getDate()));
      },
      PageDown: () => {
        moveFocus(new Date(focused().getFullYear(), focused().getMonth() + 1, focused().getDate()));
      },
      Enter: () => {
        props.onSelect(focused());
      },
      " ": () => {
        props.onSelect(focused());
      },
    };
    const handler = handlers[event.key];
    if (handler) {
      event.preventDefault();
      handler();
    }
  };

  return (
    <div class="select-none">
      <div class="flex items-center justify-between px-1 pb-2">
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t(msg`Previous month`)}
          onClick={() => {
            shiftMonth(-1);
          }}
        >
          <ChevronLeft class="h-4 w-4" />
        </button>
        <span class="text-sm font-semibold capitalize">{monthLabel()}</span>
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t(msg`Next month`)}
          onClick={() => {
            shiftMonth(1);
          }}
        >
          <ChevronRight class="h-4 w-4" />
        </button>
      </div>

      <div class="grid grid-cols-7 gap-0.5 px-1 pb-1">
        <For each={weekdayLabels()}>
          {(label) => (
            <span class="flex h-7 items-center justify-center text-[11px] font-medium uppercase text-muted-foreground">
              {label}
            </span>
          )}
        </For>
      </div>

      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
      <div
        ref={setGridRef}
        role="grid"
        aria-label={monthLabel()}
        class="grid grid-cols-7 gap-0.5 px-1"
        onKeyDown={onKeyDown}
      >
        <For each={weeks()}>
          {(week) => (
            <For each={week}>
              {(day) => {
                const isCurrentMonth = () => day.getMonth() === view().month;
                const isSelected = () => {
                  const sel = props.selected();
                  return sel ? isSameLocalDay(sel, day) : false;
                };
                const isToday = () => isSameLocalDay(today, day);
                const isFocusTarget = () => isSameLocalDay(focused(), day);
                return (
                  <button
                    type="button"
                    role="gridcell"
                    data-day={toLocalDateKey(day)}
                    tabindex={isFocusTarget() ? 0 : -1}
                    aria-selected={isSelected()}
                    aria-label={fullDateLabel(day)}
                    class={cn(
                      "relative flex h-9 w-9 items-center justify-center rounded-lg text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      isCurrentMonth() ? "text-foreground" : "text-muted-foreground/40",
                      isSelected()
                        ? "bg-primary text-primary-foreground font-semibold hover:bg-primary"
                        : "hover:bg-muted/20",
                    )}
                    onClick={() => {
                      setFocused(startOfDay(day));
                      props.onSelect(day);
                    }}
                  >
                    {day.getDate()}
                    <Show when={isToday() && !isSelected()}>
                      <span class="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-primary" />
                    </Show>
                  </button>
                );
              }}
            </For>
          )}
        </For>
      </div>
    </div>
  );
}
